/**
 * DeleteDocumentDialog — type-ARCHIVE confirmation gate for individual document
 * archival from the Documents Library.
 *
 * Renamed 2026-05-05 (CR-2026-DOCUMENTS-ARCHIVE-RESTORE) from "Delete" to
 * "Archive" semantics. The backend behavior is unchanged (soft-delete via
 * Sequelize paranoid:true) but the UX language now matches the spec: the
 * document is hidden from the active list but can be restored at any time
 * via the Archived filter + restore icon.
 *
 * Component name kept as DeleteDocumentDialog for import-path stability;
 * the language inside is what changed.
 *
 *   - Type "ARCHIVE" gate before the confirm button enables
 *   - Mandatory reason text field (audit trail)
 *   - "Document is hidden from the active list but can be restored" copy
 *   - File metadata preview (filename, PMK, status)
 *   - Frontend-side guard: refuses to render confirm button if status === 'approved'
 *     (server is still authoritative; this is defense-in-depth)
 *
 * AISC 303 §4 / ISO 9001 §7.5.3 records preservation: approved drawings
 * cannot be archived via this UI.
 */

import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Alert, Box, Chip, Stack,
} from "@mui/material";
import { Inventory2Outlined, WarningAmberRounded } from "@mui/icons-material";
import prettifyDocumentName from "../../utils/prettifyDocumentName";

const TYPE_GATE_WORD = "ARCHIVE";

export default function DeleteDocumentDialog({
  open,
  document,
  onClose,
  onConfirm,
  busy = false,
  error = null,
}) {
  const [typeGate, setTypeGate] = useState("");
  const [reason, setReason] = useState("");
  const [localError, setLocalError] = useState(null);

  // Reset when dialog opens for a new document
  useEffect(() => {
    if (open) {
      setTypeGate("");
      setReason("");
      setLocalError(null);
    }
  }, [open, document?.id]);

  if (!document) return null;

  const status = document.approval_status || "approved";
  const isApproved = status === "approved";
  const reasonOk = reason.trim().length >= 4;
  const gateOk = typeGate.trim().toUpperCase() === TYPE_GATE_WORD;
  const canConfirm = !isApproved && reasonOk && gateOk && !busy;

  const handleConfirm = () => {
    if (isApproved) {
      setLocalError("Approved drawings cannot be archived (records preservation).");
      return;
    }
    if (!reasonOk) {
      setLocalError("Reason must be at least 4 characters.");
      return;
    }
    if (!gateOk) {
      setLocalError(`Type ${TYPE_GATE_WORD} to confirm.`);
      return;
    }
    setLocalError(null);
    onConfirm?.(reason.trim());
  };

  // Display-cleaned filename (strips upload _<timestamp> suffix, .pdf
  // extension, URL-encoded escapes). Tooltip shows full pretty name.
  const fileName = prettifyDocumentName(document);
  const pmk = document.pmk_number || document.pmk || "—";

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "warning.dark" }}>
        <Inventory2Outlined />
        Archive document?
      </DialogTitle>
      <DialogContent>
        {isApproved && (
          <Alert severity="error" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Approved drawings cannot be archived
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Records preservation per AISC 303 §4 / ISO 9001 §7.5.3 — approved
              drawings must be retained. To remove this document from the active
              list, first supersede it with a new revision (sets approval_status
              to <code>superseded</code>).
            </Typography>
          </Alert>
        )}

        <Box sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
              {fileName}
            </Typography>
            <Chip size="small" label={status.replace("_", " ")} variant="outlined" />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            PMK: {pmk} · Job #{document.job_id || "—"} · Rev {document.revision || "—"} · ID #{document.id}
          </Typography>
        </Box>

        {!isApproved && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Archive</strong> — document is hidden from the active
                list but can be restored at any time by an admin. Filter the
                Documents Library by Approval Status &ldquo;Archived&rdquo; to
                find archived rows. The audit_log entry persists indefinitely.
              </Typography>
            </Alert>

            <TextField
              fullWidth
              required
              multiline
              rows={2}
              label="Reason for archival (required, min 4 chars)"
              placeholder="e.g., Uploaded to wrong job; superseded by external file; user error"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={reason.length > 0 && !reasonOk}
              helperText={`${reason.trim().length} / min 4`}
              sx={{ mb: 2 }}
              autoFocus
              disabled={busy}
            />

            <TextField
              fullWidth
              required
              label={`Type ${TYPE_GATE_WORD} to confirm`}
              value={typeGate}
              onChange={(e) => setTypeGate(e.target.value)}
              error={typeGate.length > 0 && !gateOk}
              helperText={
                gateOk
                  ? "Confirmed."
                  : `Exact match required (case-insensitive).`
              }
              disabled={busy}
            />
          </>
        )}

        {(localError || error) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {localError || (error?.message || String(error)).slice(0, 300)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {isApproved ? "Close" : "Cancel"}
        </Button>
        {!isApproved && (
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            color="warning"
            variant="contained"
            startIcon={<Inventory2Outlined />}
          >
            {busy ? "Archiving…" : "Archive document"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
